import { useCallback, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type {
  Appointment,
  Coverage,
  CoverageEligibilityResponse,
  Encounter,
  Patient,
} from '@medplum/fhirtypes'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { normalizeResolvedCoverageSlots } from '@/utils/coverageUtils'
import { getResourceIdFromReference } from '@/utils/insuranceCardUtils'
import { attachedCoverageReference } from '@/utils/selfPay'
import { resolveCoverageInsuranceCardImageUrls } from '@/utils/documentReferenceUtils'
import type { InsuranceCardSide } from '@/types/insuranceTypes'
import {
  fetchLatestInsuranceOrgDecision,
  resolveCurrentOrganizationId,
  type InsuranceOrgDecision,
} from '@/utils/insuranceQuestionnaire'
import { logError } from '@/utils/errorUtils'

type ResolvedCoveragesResult = {
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

const INSURANCE_LABELS = ['Primary', 'Secondary', 'Tertiary'] as const

export type CoverageSelect = { label: string; value: string; coverage: Coverage }

type State = {
  patient: Patient | null
  appointment: Appointment | null
  coverage: Coverage | null
  /** All coverages resolved for the patient — the options for the Change-insurance selector. */
  coverages: CoverageSelect[]
  coverageResponse: CoverageEligibilityResponse | null
  cardImages: Record<InsuranceCardSide, string | null>
  /** true = signed, false = not signed, null = unknown/undeterminable. */
  consentSigned: boolean | null
  /** Latest clinic-level insurance acceptance decision, or null when none recorded. */
  decision: InsuranceOrgDecision | null
  loading: boolean
}

export type AppointmentInsurance = State & { refresh: () => void }

const EMPTY: State = {
  patient: null,
  appointment: null,
  coverage: null,
  coverages: [],
  coverageResponse: null,
  cardImages: { front: null, back: null },
  consentSigned: null,
  decision: null,
  loading: false,
}

/**
 * Loads the attached insurance coverage, its latest eligibility response, the coverage's
 * card images, and the patient's consent status for an encounter — mirroring
 * AppointmentDetailsModal's `loadState` business logic:
 * encounter → appointment → patient → resolved coverages (middleware) → attached coverage →
 * eligibility response + card images + consent (via the practitioner's organization).
 *
 * When no coverage is attached to the appointment, `coverage` stays null (the card
 * shows "No coverage attached"); the full coverage list is still returned as the
 * options for the Change/Assign selector.
 */
export function useAppointmentInsurance(encounterId: string | null): AppointmentInsurance {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const orpcClient = useClient()
  const [state, setState] = useState<State>({
    ...EMPTY,
    loading: Boolean(encounterId),
  })
  const [reloadKey, setReloadKey] = useState(0)
  const refresh = useCallback(() => setReloadKey((key) => key + 1), [])

  useEffect(() => {
    if (!encounterId) {
      setState(EMPTY)
      return
    }

    const middlewareClient = middleware as unknown as {
      get: <T = unknown>(url: string) => Promise<{ result: T }>
    }
    let cancelled = false
    setState((prev) => ({ ...prev, loading: true }))

    const loadEligibility = async (
      coverage: Coverage | null,
      patientId?: string,
    ): Promise<CoverageEligibilityResponse | null> => {
      if (!coverage?.id || !patientId) return null
      const coverageRef = `Coverage/${coverage.id}`
      const requests = await medplum.searchResources(
        'CoverageEligibilityRequest',
        `patient=Patient/${patientId}&_sort=-_lastUpdated&_count=100`,
      )
      const latestRequest = requests.find((request) =>
        request.insurance?.some((insurance) => insurance.coverage?.reference === coverageRef),
      )
      if (!latestRequest?.id) return null
      return (
        (await medplum.searchOne(
          'CoverageEligibilityResponse',
          `request=CoverageEligibilityRequest/${latestRequest.id}&_sort=-_lastUpdated`,
        )) ?? null
      )
    }

    const loadConsent = async (
      encounter: Encounter,
      patientId?: string,
    ): Promise<boolean | null> => {
      const practitionerRef = encounter.participant?.[0]?.individual?.reference
      if (!practitionerRef || !patientId) return null
      const practRole = await medplum.searchOne(
        'PractitionerRole',
        `practitioner=${practitionerRef}`,
      )
      const organizationId = practRole?.organization?.reference?.split('/').pop()
      if (!organizationId) return null
      try {
        const consent = await orpcClient.consentGenerate({ patientId, organizationId })
        return Boolean(consent.isSigned)
      } catch (error) {
        // A transport/auth/service failure means the consent state is unknown,
        // not "not signed" — return null so the badge reads "Consent: —" rather
        // than affirmatively reporting missing consent.
        logError('appointments', 'Check appointment consent (preview)', error)
        return null
      }
    }

    const loadDecision = async (
      coverage: Coverage | null,
      patientId?: string,
    ): Promise<InsuranceOrgDecision | null> => {
      if (!coverage?.id || !patientId) return null
      const organizationId = await resolveCurrentOrganizationId(medplum, medplum.getProfile()?.id)
      if (!organizationId) return null
      return (
        (await fetchLatestInsuranceOrgDecision(medplum, {
          patientId,
          coverageId: coverage.id,
          organizationId,
        })) ?? null
      )
    }

    // On an explicit refresh (after a mutation such as changing insurance),
    // bypass Medplum's request cache so the encounter/appointment are re-read
    // from the server and the popup reflects the latest state.
    const freshOptions = reloadKey > 0 ? ({ cache: 'reload' } as const) : undefined

    void (async () => {
      try {
        const encounter = await medplum.readResource('Encounter', encounterId, freshOptions)
        const appointmentId = encounter.appointment?.[0]?.reference?.split('/')[1]
        const appointment = appointmentId
          ? ((await medplum.readResource(
              'Appointment',
              appointmentId,
              freshOptions,
            )) as Appointment)
          : null

        if (!encounter.subject) {
          if (!cancelled) setState({ ...EMPTY, loading: false })
          return
        }

        const patient = (await medplum.readReference(encounter.subject)) as Patient
        const { result } = await middlewareClient.get<ResolvedCoveragesResult>(
          `/insurance/patient/${patient.id}/coverages`,
        )
        const resolvedCoverages = normalizeResolvedCoverageSlots(result)
        const coverages: CoverageSelect[] = resolvedCoverages
          .filter((item): item is Coverage & { id: string } => Boolean(item.id))
          .map((item, index) => ({
            label: `${INSURANCE_LABELS[index] ?? 'Coverage'}: ${item.payor?.[0]?.display || 'Unknown Insurance'}`,
            value: item.id,
            coverage: item,
          }))

        const attachedRef = appointment ? attachedCoverageReference(appointment) : undefined
        // Only the coverage the appointment actually references counts as
        // attached. Never fall back to the patient's primary policy: doing so
        // showed eligibility/clinic-decision data for a coverage the
        // appointment doesn't reference, and made "clear insurance" appear to
        // silently re-attach the primary on refresh.
        let coverage: Coverage | null = null
        if (attachedRef) {
          const attachedId = getResourceIdFromReference(attachedRef)
          coverage =
            resolvedCoverages.find((item) => item.id === attachedId) ??
            ((await medplum.readReference({ reference: attachedRef })) as Coverage)
        }

        const [coverageResponse, cardImages, consentSigned, decision] = await Promise.all([
          loadEligibility(coverage, patient.id),
          resolveCoverageInsuranceCardImageUrls(medplum, coverage),
          loadConsent(encounter, patient.id),
          loadDecision(coverage, patient.id),
        ])

        if (!cancelled) {
          setState({
            patient,
            appointment,
            coverage,
            coverages,
            coverageResponse,
            cardImages,
            consentSigned,
            decision,
            loading: false,
          })
        }
      } catch (error) {
        logError('appointments', 'Load appointment preview insurance', error)
        if (!cancelled) setState({ ...EMPTY, loading: false })
      }
    })()

    return () => {
      cancelled = true
    }
  }, [encounterId, medplum, middleware, orpcClient, reloadKey])

  return { ...state, refresh }
}
